import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, distinctUntilChanged, EMPTY, finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { HistoryInteraction } from '../../../model/common/history-interaction.model';
import { Interaction } from '../../../model/common/interaction.model';
import { CountryEnum } from '../../../model/converted-dialog/country.enum';
import { ProcessLandingConversionDto } from '../../../model/interaction/process-landing-conversion.dto';
import { LandingConversion } from '../../../model/landing-conversion/landing-conversion.model';
import { CatalogCollection } from '../../../model/mail-classifier/catalog-collection.model';
import { ClientRepresentative } from '../../../model/mail-classifier/client-representative.model';
import { LeadGenSources } from '../../../model/mail-classifier/lead-gen-sources.model';
import { ConfigurationService } from '../../../services/configuration.service';
import { LandingConversionsService } from '../../../services/landing-conversions.service';
import { MailReviewService } from '../../../services/mail-review.service';
import { SnackBarService } from '../../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-conversion-form',
  templateUrl: './conversion-form.component.html',
  styleUrl: './conversion-form.component.scss',
  standalone: false,
})
export class ConversionFormComponent implements OnInit, OnDestroy {
  @Input() selectedInteraction: Interaction = null;
  @Input() selectedHistoryInteraction: HistoryInteraction = null;
  @Output() convertedEvent = new EventEmitter<ProcessLandingConversionDto>(null);
  protected conversion: LandingConversion;
  protected convertedForm: FormGroup;
  protected clientRepresentatives: ClientRepresentative;
  protected catalogs: CatalogCollection = null;
  protected leadGenSources: LeadGenSources[] = [];
  private destroy$ = new Subject<boolean>();

  get bdsourceTooltip() {
    return this.leadGenSources.find((source) => source?.id === this.convertedForm?.get('bdSourceId')?.value)?.name;
  }

  constructor(
    private fb: FormBuilder,
    private configurationService: ConfigurationService,
    private landingConversionsService: LandingConversionsService,
    private mailReviewService: MailReviewService,
    private snackBarService: SnackBarService,
  ) {
    this.loadCatalogs();
  }

  ngOnInit(): void {
    this.createForm();
    this.formObs$();
    this.selectedConversionObs$();
    this.clientRepresentativeObs$();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private selectedConversionObs$() {
    this.landingConversionsService.selectedConversion$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (conversion) => {
        this.conversion = conversion;

        if (conversion) {
          this.patchFormValues();
        }
      },
    });
  }

  private patchFormValues() {
    const { id, customerId, email, company, firstName, lastName, linkedInUrl } = this.conversion || {};

    this.convertedForm?.patchValue({
      id: id || '',
      customerId: customerId || '',
      email: email || '',
      domain: email?.split('@')[1] || '',
      company: company || '',
      firstName: firstName || '',
      lastName: lastName || '',
      linkedIn: linkedInUrl || '',
    });
  }

  private clientRepresentativeObs$() {
    this.configurationService.clientRepresentatives$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (clientRepresentatives) => {
        if (clientRepresentatives) {
          this.clientRepresentatives = clientRepresentatives;
        }

        if (this.leadGenSources) {
          this.leadGenSources = this.leadGenSources.filter(
            (source) => source.customerId === this.clientRepresentatives?.clientId,
          );
        }

        if (!!clientRepresentatives && !this.clientRepresentatives?.representatives?.length) {
          this.convertedForm.get('salesRepId').disable();
          this.snackBarService.showError('No client representatives found');
        }
      },
    });
  }

  formObs$() {
    this.convertedForm
      .get('bdMatrixId')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(() => {
        this.calculateScore();
      });

    this.convertedForm
      .get('bdDelayId')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(() => this.calculateScore());

    this.convertedForm
      .get('bdCountryId')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(() => this.calculateScore());

    this.convertedForm
      .get('bdIndustryId')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(() => this.calculateScore());

    this.convertedForm
      .get('bdSourceId')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(() => this.calculateScore());
  }

  private createForm() {
    const { customerId, email, company, firstName, lastName, linkedInUrl } = this.conversion || {};

    this.convertedForm = this.fb.group({
      customerId: [{ value: customerId || '', disabled: true }],
      email: [email || '', Validators.required],
      domain: [email?.split('@')[1] || ''],
      company: [company || ''],
      firstName: [firstName || '', Validators.required],
      lastName: [lastName || '', Validators.required],
      linkedIn: [linkedInUrl || ''],
      bdSourceId: [null, Validators.required],
      bdMatrixId: [null, Validators.required],
      bdCountryId: [null, Validators.required],
      bdDelayId: [null, Validators.required],
      bdIndustryId: [null, Validators.required],
      salesRepId: [null, Validators.required],
      businessValue: [{ value: null, disabled: true }],
    });
  }

  private loadCatalogs() {
    forkJoin({
      catalogs: this.configurationService.getCatalogs().pipe(
        catchError(() => {
          this.snackBarService.showError('An error occurred while trying to get the catalogs');
          return EMPTY;
        }),
      ),
      leadGenSources: this.configurationService.getLeadGenSources().pipe(
        catchError(() => {
          this.snackBarService.showError('An error occurred while trying to get the sources');
          return EMPTY;
        }),
      ),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.calculateScore()),
      )
      .subscribe(({ catalogs, leadGenSources }) => {
        if (catalogs) {
          this.catalogs = catalogs;
          this.setDefaultBdCountryId();
        }

        if (leadGenSources) {
          this.leadGenSources = leadGenSources;
        }

        this.calculateScore();
      });
  }

  private setDefaultBdCountryId() {
    if (!this.convertedForm.get('bdCountryId').value) {
      const countryName = this.conversion.country === 'US' ? CountryEnum.UnitedStates : CountryEnum.NonUS;
      const defaultBdCountryId = this.catalogs?.country.find((country) => country.name === countryName)?.id;

      this.convertedForm.get('bdCountryId').setValue(defaultBdCountryId || null);
    }
  }

  private calculateScore() {
    if (this.catalogs && this.leadGenSources?.length) {
      const { bdMatrixId, bdDelayId, bdCountryId, bdIndustryId, bdSourceId } = this.convertedForm.getRawValue();
      const bdMatrixValue = this.catalogs?.matrix?.find((matrix) => matrix.id === bdMatrixId)?.value;
      const bdDelayValue = this.catalogs?.delay?.find((delay) => delay.id === bdDelayId)?.value;
      const bdCountryValue = this.catalogs?.country?.find((country) => country.id === bdCountryId)?.value;
      const bdIndustryValue = this.catalogs?.industry?.find((industry) => industry.id === bdIndustryId)?.value;
      const bdSourceIdFactor = this.leadGenSources?.find((source) => source.id === bdSourceId)?.factor;

      if (bdMatrixId && bdDelayId && bdCountryId && bdIndustryId && bdSourceId) {
        const calculateScore = bdMatrixValue * bdDelayValue * bdCountryValue * bdIndustryValue * bdSourceIdFactor;
        this.convertedForm.patchValue({ businessValue: calculateScore ? this.formatNumber(calculateScore) : 0 });
      }
    }
  }

  private formatNumber(value: number) {
    return Number.isInteger(value) ? value.toString() : value.toFixed(3);
  }

  converted() {
    const processLandingConversionDto: ProcessLandingConversionDto = {
      registerConversionPayload: {
        ...this.convertedForm.getRawValue(),
        contactId: this.selectedInteraction.customerId,
        sdrId: this.selectedInteraction.sdrId,
        interactionId: this.selectedInteraction?.interactionId,
        messageId: this.selectedInteraction?.messageId,
        interactionUtcDatetime: this.selectedInteraction?.utcDatetimeInteraction,
        conversionType: 1, // Assuming 1 is the type for landing conversion
        campaignId: this.selectedHistoryInteraction?.campaignId || this.selectedInteraction?.campaignId,
      },
      blacklistEmailId: this.conversion.id,
    };

    if (!Object.values(processLandingConversionDto).every((value) => value)) {
      this.snackBarService.showError(
        'An error occurred while retrieving the information required to process the conversion',
      );
      return;
    }

    this.convertedEvent.emit(processLandingConversionDto);
  }
}
